import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Play, Square, Clock } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * Compact clock-in/out widget shown in the sidebar footer.
 * - If the user has no open shift: shows "Clock In"
 * - If the user has an open shift: shows elapsed timer + "Clock Out"
 */
export default function ClockWidget() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(false);
  const [entry, setEntry] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  const loadCurrent = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/time/current');
      setActive(!!res.data.active);
      setEntry(res.data.entry || null);
    } catch (err) {
      // Silently ignore 401/403 (non-agent user, etc.)
      setActive(false);
      setEntry(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCurrent();
  }, [loadCurrent]);

  // Tick every second while active
  useEffect(() => {
    if (!active || !entry?.clock_in) {
      setElapsedSec(0);
      return;
    }
    const start = new Date(entry.clock_in).getTime();
    const tick = () => setElapsedSec(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active, entry]);

  const handleClockIn = async () => {
    try {
      setBusy(true);
      await api.post('/time/clock-in', {});
      toast.success('Clocked in');
      await loadCurrent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock in');
    } finally {
      setBusy(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setBusy(true);
      const res = await api.post('/time/clock-out', {});
      const mins = res.data?.duration_minutes || 0;
      toast.success(`Clocked out — ${(mins / 60).toFixed(2)}h logged`);
      await loadCurrent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock out');
    } finally {
      setBusy(false);
    }
  };

  const formatElapsed = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 px-3 py-2" data-testid="clock-widget-loading">
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading…
      </div>
    );
  }

  if (active) {
    return (
      <div className="px-3 py-2 rounded-lg border border-cyan-200 bg-cyan-50/60" data-testid="clock-widget-active">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
            </span>
            <span className="text-xs font-medium text-cyan-700">On shift</span>
          </div>
          <span className="text-xs font-mono text-cyan-900" data-testid="clock-elapsed">
            {formatElapsed(elapsedSec)}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="w-full h-8 rounded-full border-red-300 text-red-600 hover:bg-red-50"
          onClick={handleClockOut}
          disabled={busy}
          data-testid="clock-out-btn"
        >
          {busy ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Square className="w-3 h-3 mr-2" />}
          Clock Out
        </Button>
      </div>
    );
  }

  return (
    <div className="px-3 py-2" data-testid="clock-widget-idle">
      <Button
        size="sm"
        className="w-full h-8 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white"
        onClick={handleClockIn}
        disabled={busy}
        data-testid="clock-in-btn"
      >
        {busy ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Play className="w-3 h-3 mr-2" />}
        Clock In
      </Button>
    </div>
  );
}
